import { useQueryClient } from '@tanstack/react-query';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo, useReducer, useState } from 'react';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { BookingHintsContext, BookingSkeleton, bookingSkeletonHeight, CentersSkeleton, ProfileHeadSkeleton, type HeadSeed } from '@/modules/profile/components/ProfileSectionSkeleton';
import { ProfileHead } from '@/modules/profile/components/ProfileHead';
import { assembleProfile } from '@/modules/profile/functions/assembleProfile';
import { BASE_OPTIONS, prefetchBookingPieces } from '@/modules/profile/hooks/useProgressiveProfile';
import { findSearchDoctor, seedPiecesFromSearchDoctor } from '@/modules/profile/utils/seedPiecesFromSearch';
import { buildDoctorExpertiseChips } from '@/common/utils/buildDoctorExpertiseChips';
import { formatAverageRate } from '@/modules/profile/utils/getAverageRate';
import { shouldShowProfileHeadRateBox } from '@/modules/profile/utils/shouldShowProfileHeadRateBox';
import useCustomize from '@/common/hooks/useCustomize';
import { getProfileSeedFromSearch } from '@/modules/profile/utils/profileSeedFromSearch';
import { profileSlugFromUrl } from '@/modules/profile/utils/profileSlugFromUrl';
import { loadedPreviewBooking, preloadProfileAsideCode } from '@/modules/profile/functions/preloadProfileAsideCode';
import { markNavigationPreviewShown } from '@/common/services/navigation/routeFade';
import { MEMBERSHIP_NUMBER_LABEL } from '@/modules/profile/utils/buildAboutBiographyWithMedicalCode';
import { isPsychologist } from '@/modules/profile/utils/isPsychologist';

export { profileSlugFromUrl };

/** A tap counts as the start of the navigation it triggers for this long. */
const TAP_TO_ROUTE_START_MS = 3000;

type Preview = { slug: string; decoded: string; seed: HeadSeed | null; headerHtml: string; head: Record<string, any> | null };

/** New app: the real page's first paint has the app bar (back) and the section tabs; the preview draws the same two, so nothing is added or moved when the real page takes over. */
const PreviewAppBar = () => (
  <div className="flex h-14 min-h-14 items-center bg-white px-5 ps-1 text-[#222]">
    <div className="me-3 flex h-12 w-12 items-center justify-center rounded-full">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.43 5.93 20.5 12l-6.07 6.07M3.5 12h16.83" />
      </svg>
    </div>
  </div>
);

const PREVIEW_TABS = ['نوبت', 'آدرس و تلفن', 'درباره', 'نظرات'];
const PreviewSectionTabs = () => (
  <nav className="mb-3 flex h-11 w-full bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]">
    {PREVIEW_TABS.map((label, index) => (
      <div key={label} className={`relative flex flex-1 items-center justify-center text-[14px] leading-5 ${index === 0 ? 'font-medium text-primary' : 'text-slate-500'}`}>
        {label}
        {index === 0 && <span className="absolute bottom-0 h-[3px] w-10 rounded-t-full bg-primary" />}
      </div>
    ))}
  </nav>
);

const PreviewHead = memo(({ preview, showRateAndReviews }: { preview: Preview; showRateAndReviews?: boolean }) =>
  preview.head ? (
    <div className="flex flex-col gap-px">
      <ProfileHead
        {...(preview.head as any)}
        rate={!shouldShowProfileHeadRateBox(showRateAndReviews) ? null : preview.head.rate === undefined ? 'pending' : { ...preview.head.rate, rate: showRateAndReviews !== false && preview.head.rate.rate }}
        reserveRateHeight
      />
    </div>
  ) : <ProfileHeadSkeleton seed={preview.seed} />,
);

/**
 * Instant first frame for search → profile on mobile.
 *
 * Next renders the new route inside a React transition. On a mid-range phone (CPU ×4) that render
 * competes with third-party scripts reacting to the URL change and only commits after ~5s (when the
 * transition expires) — all that time the user saw the search page, then a dimmed 3-dot overlay.
 * Instead, the moment the tapped navigation starts, this paints what the profile's first frame will
 * be: the site header, the profile head filled from the tapped card (name, the card's already-cached
 * photo, chips), the real booking card when a pause/tap already prefetched its data (PreviewBooking,
 * else its skeleton) and the address skeleton, at the exact positions of the real page
 * (measured at 412px). When the profile commits, the search page — and this layer with it — is
 * hidden (keep-alive), so the real page simply takes its place.
 *
 * Only for taps (push navigations): Back/Forward to a cached profile renders without skeletons,
 * so no preview there. Decorative only (`aria-hidden`, no pointer events) — nothing on it can be
 * clicked or tracked. Below `lg`; desktop keeps the slow-navigation overlay.
 */
export const ProfileNavigationPreview = () => {
  const router = useRouter();
  const isApp = useIsNewAppShell();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<Preview | null>(null);
  // Re-render when the booking data or the preview booking code lands while the preview is up.
  const [bookingRevision, refresh] = useReducer((n: number) => n + 1, 0);
  const customize = useCustomize(state => state.customize);
  const university = customize?.partnerKey || undefined;

  useEffect(() => {
    let tappedPath: string | null = null;
    let tappedAt = 0;
    const onClick = (event: MouseEvent) => {
      const anchor = event.target instanceof Element ? (event.target.closest('a[href]') as HTMLAnchorElement | null) : null;
      if (!anchor) return;
      try {
        const url = new URL(anchor.href, location.href);
        if (url.origin !== location.origin) return;
        tappedPath = url.pathname;
        tappedAt = Date.now();
      } catch {
        tappedPath = null;
      }
    };
    const onStart = (url: string) => {
      const slug = profileSlugFromUrl(url);
      const path = url.split(/[?#]/)[0];
      const tapped = !!tappedPath && Date.now() - tappedAt < TAP_TO_ROUTE_START_MS && decodeURI(tappedPath) === decodeURI(path);
      // Already visited this doctor: the profile renders straight from the query cache, so a
      // skeleton preview would only flash over data we already have.
      // Keyed on 'name', which prefetchBookingPieces never fetches: a prefetched-but-abandoned tap
      // must not count as a visit.
      let cached = false;
      let decoded = slug;
      if (slug) {
        try {
          decoded = decodeURIComponent(slug);
        } catch { /* Keep the original slug when it is not valid URI encoding. */ }
        cached = queryClient
          .getQueryCache()
          .findAll(['profilePiece', 'name'])
          .some(q => (q.queryKey[2] === decoded || q.queryKey[2] === slug) && q.state.data !== undefined);
      }
      // The booking card's data, from the tap instead of from the profile's (late) commit. Any tapped
      // profile, desktop too; a revisit within staleTime is a no-op.
      if (decoded && tapped) {
        try {
          prefetchBookingPieces(queryClient, decoded, university);
          void preloadProfileAsideCode();
        } catch {
          // Best-effort; never let a prefetch break the navigation.
        }
      }
      if (!slug || !tapped || cached || window.matchMedia?.('(min-width: 1024px)').matches) {
        setPreview(null);
        return;
      }
      const header = document.querySelector('header');
      markNavigationPreviewShown(); // it fades in on its own (no View Transition)
      setPreview({
        slug,
        decoded: decoded ?? slug,
        seed: getProfileSeedFromSearch(queryClient, slug),
        headerHtml: header?.outerHTML ?? '',
        head: buildPreviewHead(queryClient, slug),
      });
    };
    const onDone = () => setPreview(null);
    document.addEventListener('click', onClick, { capture: true, passive: true });
    router.events.on('routeChangeStart', onStart);
    router.events.on('routeChangeComplete', onDone);
    router.events.on('routeChangeError', onDone);
    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      router.events.off('routeChangeStart', onStart);
      router.events.off('routeChangeComplete', onDone);
      router.events.off('routeChangeError', onDone);
    };
  }, [router.events, queryClient, university]);

  const bookingKeys = useMemo(() => preview ? [
    ...['centers', 'online', 'waiting'].map(piece => ['profilePiece', piece, preview.decoded, university ?? null, preview.decoded]),
    [ServerStateKeysEnum.SamanDoctorAvailabilityStatus, preview.decoded],
  ] : [], [preview, university]);
  const bookingData = useMemo(() => bookingKeys.map(key => queryClient.getQueryData(key)), [bookingKeys, queryClient, bookingRevision]);
  useEffect(() => {
    if (!preview) return;
    // Assembly reads only these pieces. Status changes and the header/review queries must
    // not rebuild the whole booking tree. Its own child queries subscribe independently.
    const keys = bookingKeys;
    let data = bookingData;
    let live = true;
    const reconcile = () => {
      const next = keys.map(key => queryClient.getQueryData(key));
      if (next.some((value, i) => value !== data[i])) { data = next; refresh(); }
    };
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      if (!live || !event || !keys.some(key => key.length === event.query.queryKey.length && key.every((v, i) => v === event.query.queryKey[i]))) return;
      reconcile();
    });
    reconcile(); // Cover a response arriving between render and effect subscription.
    return () => { live = false; unsubscribe(); };
  }, [preview, queryClient, bookingKeys, bookingData]);

  useEffect(() => {
    if (!preview || loadedPreviewBooking()) return;
    let live = true;
    void preloadProfileAsideCode().then(() => {
      if (live && loadedPreviewBooking()) refresh();
    });
    return () => { live = false; };
  }, [preview]);

  const previewBooking = loadedPreviewBooking();
  const bookingSection = useMemo(() => {
    if (!preview) return null;
    try { return previewBooking?.buildPreviewBookingSection(queryClient, preview.decoded, customize) ?? null; }
    catch { return null; } // the preview must never break navigation
  }, [preview, previewBooking, queryClient, customize, bookingRevision]);
  if (!preview) return null;
  const previewHints = preview.seed ? { online: (preview.seed as any).online, presence: (preview.seed as any).presence } : null;
  return (
    <div
      className="lg:hidden fixed inset-0 z-[39] overflow-hidden bg-[#F2F2F2] pointer-events-none"
      aria-hidden="true"
      data-profile-preview=""
    >
      {isApp ? <PreviewAppBar /> : preview.headerHtml && <div dangerouslySetInnerHTML={{ __html: preview.headerHtml }} />}
      <div className="flex flex-col gap-3">
        <div>
          <PreviewHead preview={preview} showRateAndReviews={customize?.showRateAndReviews} />
          {isApp && <PreviewSectionTabs />}
        </div>
        {bookingSection && previewBooking ? (
          <div className="flex flex-col space-y-3">
            <previewBooking.PreviewBooking section={bookingSection} />
          </div>
        ) : (
          <BookingHintsContext.Provider value={previewHints}>
            <div style={{ height: bookingSkeletonHeight(previewHints) }}>
              <BookingSkeleton />
            </div>
          </BookingHintsContext.Provider>
        )}
        <div className="h-[663px]">
          <CentersSkeleton />
        </div>
      </div>
    </div>
  );
};

/**
 * Header args for the preview, built exactly like the profile page's: the search result becomes
 * profile pieces, assembled by the same `assembleProfile`, then the same chips/rate helpers — and
 * rendered by the same `ProfileHead` component (single source of truth).
 */
function buildPreviewHead(queryClient: ReturnType<typeof useQueryClient>, slug: string): Record<string, any> | null {
  const doctor = findSearchDoctor(queryClient, slug);
  if (!doctor) return null;
  let decoded = slug;
  try {
    decoded = decodeURIComponent(slug);
  } catch { /* Search cache also accepts an already-decoded slug. */ }
  const pieces = seedPiecesFromSearchDoctor(doctor);
  const props: any = assembleProfile({ validatedSlug: decoded, ...pieces } as any, {
    isServer: false,
    options: { ...BASE_OPTIONS, fetchExpertiseKeywordTags: true },
  }).props;
  const r: any = pieces.rateDetails;
  return {
    pageViewCount: props.history?.count_of_page_view,
    serviceList: (() => {
      const tags = pieces.keywordTags ?? [];
      const chips = buildDoctorExpertiseChips(props.expertises?.expertises, { keywordTags: tags });
      if (chips.length) return chips;
      // No structured expertises on the search item: its display text + keyword tags, like the card.
      return [...String(doctor.display_expertise ?? '').split('|'), ...tags].map(x => String(x).trim()).filter(Boolean);
    })(),
    displayName: props.information?.display_name ?? doctor.display_name,
    title: typeof doctor.experience === 'number' && doctor.experience > 1 ? `${doctor.experience - 1} سال تجربه` : undefined,
    subTitle: `${isPsychologist(props.expertises) ? MEMBERSHIP_NUMBER_LABEL : 'شماره نظام پزشکی'}: ${props.information?.employee_id ?? ''}`,
    imageUrl: pieces.image?.image,
    slug: decoded,
    rate: r ? { count: r.countOfFeedbacks ?? undefined, rate: formatAverageRate(r.averageRates), hideRates: r.hide_rates } : undefined,
  };
}
